import React, { useMemo, useState } from "react";
import {
    ActionsCard,
    ActionButtons,
    ViewUsersTableContainer,
    ActionButtonsWrapper,
    SearchBar,
    SearchInput,
} from "./ViewUsersTable.styles";
import Table from "../../../../components/table/Table";
import ChangeRoleModal from "../../../../components/modal/change-role-modal/ChangeRoleModal";
import ResetPasswordModal from "../../../../components/modal/reset-password/ResetPassword";
import AddRemoveLicenseKeyModal, {
    LicenseMutationResult,
} from "../../../../components/modal/add-remove-license-key/AddRemoveLicenseKey";
import CopyLicenceKey from "../CopyLicenceKey/CopyLicenceKey";
import { UserRow } from "../../../../core/ServerApiLayer/VOne/institutions/get_users";
import { GetLicensesType } from "../../../../core/ServerApiLayer/VOne/institutions/get_available_licenses";
import { PasswordResetRequestType } from "../../../../core/ServerApiLayer/VOne/users/request_password";

// Language Context
import { useLanguage } from "../../../../core/translations/LanguageContext";

// Translations
import { UsersPageTranslations } from "../../translations/types";
import { enTranslations } from "../../translations/en";
import { deTranslations } from "../../translations/de";
import { SupportedLanguage } from "../../../../core/translations/supportedLanguages";

const TRANSLATIONS: Record<SupportedLanguage, UsersPageTranslations> = {
    en: enTranslations,
    de: deTranslations,
};

// Enough rows to be useful on a laptop without the page growing without bound.
const ROWS_PER_PAGE = 10;

/*
Does this user match what was typed in the search box?

Matches across every column an admin can see — so pasting a licence key finds
who holds it, and typing a role narrows to that role.
*/
function matchesSearch(user: UserRow, query: string): boolean {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;

    return [user.user_name, user.role, user.license_key ?? ""].some((field) =>
        field.toLowerCase().includes(needle),
    );
}

// These are the page's *handlers*, not the raw API calls — they take a user and
// a license key, talk to the backend, and re-read the users table before they
// resolve.
type LicenseRequest = (
    userId: number,
    licenseKey: string,
) => Promise<LicenseMutationResult>;

interface ViewUsersTableProps {
    data: UserRow[];
    // Whether the users request is still out. Named apart from the `loading`
    // below, which covers fetching licences for the modal.
    loadingUsers: boolean;
    assignLicenseRequest: LicenseRequest;
    removeLicenseRequest: LicenseRequest;
    getLicenses: GetLicensesType;
    requestPassword: PasswordResetRequestType;
    handleAssignRole: (userId: number, roleName: string) => Promise<void>;
    handleUnassignRole: (userId: number, roleName: string) => Promise<void>;
}

/**
 * ViewUsersTable Component
 *
 * Displays a table of users, including their email, role, and license key.
 * Allows role changes, password resets, and license key management.
 */
export const ViewUsersTable: React.FC<ViewUsersTableProps> = ({
    data,
    loadingUsers,
    assignLicenseRequest,
    removeLicenseRequest,
    getLicenses,
    requestPassword,
    handleAssignRole,
    handleUnassignRole,
}) => {
    const [showChangeRoleModal, setShowChangeRoleModal] = useState(false);
    const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
    const [showAddRemoveLicenseModal, setShowAddRemoveLicenseModal] =
        useState(false);
    const [selectedLicenseKeyRow, setSelectedLicenseKeyRow] =
        useState<UserRow | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [search, setSearch] = useState("");

    const { language } = useLanguage();
    //@ts-ignore
    const translations: UsersPageTranslations = TRANSLATIONS[language];

    // Memoised because Table returns to page one whenever its rows change — a
    // list rebuilt on every render would pin it to the first page forever.
    const visibleUsers = useMemo(
        () => data.filter((user) => matchesSearch(user, search)),
        [data, search],
    );

    const handleResetPasswordRequest = async (email: string) => {
        setLoading(true);
        setError(null);

        try {
            // The backend picks the email template from this field.
            const response = await requestPassword({
                email,
                language,
            });

            if (response.status === 200) {
                alert("Password reset request sent successfully.");
            } else {
                setError(
                    response.message ||
                        "Failed to send password reset request.",
                );
            }
        } catch (err) {
            console.error("Error sending password reset request:", err);
            setError("An error occurred while sending password reset request.");
        }

        setLoading(false);
    };

    const columns = [
        {
            key: "user_name",
            label: translations.columns.email,
            // Sorting keys off the accessor, so a sortable column needs one —
            // without it the header's sort arrow does nothing.
            accessor: "user_name",
            sort: true,
        },
        {
            // Deliberately not sortable: the roles are a comma-joined string, so
            // sorting them would order by that text rather than by anything an
            // admin means by "sort by role".
            key: "role",
            label: translations.columns.role,
        },
        {
            key: "license_key",
            label: translations.columns.licenseKey,
            accessor: "license_key",
            sort: true,
            render: (row: UserRow) =>
                row.license_key ? (
                    <CopyLicenceKey row={{ license_key: row.license_key }} />
                ) : null,
        },
        {
            key: "actions",
            label: translations.columns.actions,
            render: (row: UserRow) => (
                <ActionsCard>
                    <ActionButtonsWrapper>
                        <ActionButtons
                            backgroundColorHex="#278230"
                            onClick={() => {
                                setSelectedLicenseKeyRow(row);
                                setShowChangeRoleModal(true);
                            }}
                        >
                            {translations.buttons.changeRole}
                        </ActionButtons>
                    </ActionButtonsWrapper>
                    <ActionButtons
                        onClick={() =>
                            handleResetPasswordRequest(row.user_name)
                        }
                    >
                        {translations.buttons.resetPassword}
                    </ActionButtons>
                    <ActionButtonsWrapper>
                        <ActionButtons
                            backgroundColorHex={
                                row.license_key ? "#cc0000" : "#278230"
                            }
                            onClick={() => {
                                setSelectedLicenseKeyRow(row);
                                setShowAddRemoveLicenseModal(true);
                            }}
                        >
                            {row.license_key
                                ? translations.buttons.removeLicenseKey
                                : translations.buttons.addNewLicenseKey}
                        </ActionButtons>
                    </ActionButtonsWrapper>
                </ActionsCard>
            ),
        },
    ];

    return (
        <ViewUsersTableContainer>
            <SearchBar>
                <SearchInput
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder={translations.search.placeholder}
                    aria-label={translations.search.placeholder}
                />
            </SearchBar>
            <Table
                data={visibleUsers}
                columns={columns}
                pageSize={ROWS_PER_PAGE}
                previousLabel={translations.pagination.previous}
                nextLabel={translations.pagination.next}
                loading={loadingUsers}
                loadingText={translations.loadingText}
                // "Nothing matched what you typed" is a different thing to say
                // than "this institution has no users".
                noRowsText={
                    search.trim()
                        ? translations.search.noMatches
                        : translations.noRecordText
                }
            />
            {showChangeRoleModal && selectedLicenseKeyRow && (
                <ChangeRoleModal
                    onRequestClose={() => setShowChangeRoleModal(false)}
                    selectedUser={selectedLicenseKeyRow}
                    handleAssignRole={handleAssignRole}
                    handleUnassignRole={handleUnassignRole}
                />
            )}
            {showResetPasswordModal && (
                <ResetPasswordModal
                    onRequestClose={() => setShowResetPasswordModal(false)}
                />
            )}
            {showAddRemoveLicenseModal && selectedLicenseKeyRow && (
                <AddRemoveLicenseKeyModal
                    onRequestClose={() => setShowAddRemoveLicenseModal(false)}
                    selectedLicenseKeyRow={selectedLicenseKeyRow}
                    getLicenses={getLicenses}
                    handleAssignLicense={assignLicenseRequest}
                    handleUnassignLicense={removeLicenseRequest}
                />
            )}
        </ViewUsersTableContainer>
    );
};

export default ViewUsersTable;
